import { useCallback, useContext, useMemo, useSyncExternalStore } from "react";

import { AccountStatusSnapshot } from "@/hooks/workspace/accountSnapshots";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { store } from "@/store/store";
import type { AccountWithWsMembershipAvailability } from "@/store/types";

/**
 * One account's status row in the current workspace, re-rendering only when that row's inputs change.
 *
 * The inputs are the account row, its membership, its availability and its alias; a write to any other row reads
 * an identical snapshot, so React bails out.
 *
 * @param accountId - The account to read; a missing id reads as null.
 * @returns The merged status row, or null when the account or its membership in the workspace is missing.
 */
export function useAccountStatus(accountId: string | null | undefined): AccountWithWsMembershipAvailability | null {
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const snapshot = useMemo(
    () => new AccountStatusSnapshot(currentWorkspaceId, accountId),
    [currentWorkspaceId, accountId],
  );
  const subscribe = useCallback((listener: () => void) => store.subscribeWrites(listener), []);
  return useSyncExternalStore(subscribe, snapshot.read);
}
